import type { OnlineDevice } from '@/@types/device';
import type { Trip } from '@/@types/gtfs-ressource';
import type { TripUpdate } from '@/@types/trip-updates';
import { previousGtfsDate } from '@pysae/transit-core/service-day';
import type { Network, NetworkTrip } from './network';

/** What is known live about one trip: its vehicle, its trip-update, or both. */
export interface TripRealtime {
  device?: Pick<OnlineDevice, 'delay'>;
  tripUpdate?: Pick<TripUpdate, 'delay' | 'delays' | 'is_canceled' | 'skipped_stop_time_seqs'>;
}

/**
 * Delay at a stop of the trip, in seconds: the vehicle's own delay first, then the
 * trip-update's delay at that stop added to its delay for the whole trip.
 * `undefined` when nothing live is known, the trip then runs on its schedule.
 */
export function stopDelay(stopSequence: number, realtime: TripRealtime | undefined): number | undefined {
  if (realtime?.device?.delay != null) return realtime.device.delay;
  const tripUpdate = realtime?.tripUpdate;
  if (!tripUpdate) return undefined;
  const atStop = tripUpdate.delays?.find(delay => delay.stop_sequence === stopSequence)?.delay ?? 0;
  return atStop + (tripUpdate.delay ?? 0);
}

/** Estimated departure from a stop of a trip, in epoch seconds; the schedule without live data. */
export function estimatedStopTime(
  trip: Pick<Trip, 'midnight' | 'stop_times'>,
  stopSequence: number,
  realtime: TripRealtime | undefined,
  kind: 'arrival' | 'departure' = 'departure',
): number | undefined {
  const stopTime = trip.stop_times.find(st => st.stop_sequence === stopSequence);
  if (!stopTime) return undefined;
  const scheduled = trip.midnight + (kind === 'arrival' ? stopTime.arrival_time : stopTime.departure_time);
  return scheduled + (stopDelay(stopSequence, realtime) ?? 0);
}

export function isStopServed(stopSequence: number, realtime: TripRealtime | undefined): boolean {
  return !realtime?.tripUpdate?.skipped_stop_time_seqs?.includes(stopSequence);
}

export interface RealtimeSnapshot {
  devices: OnlineDevice[];
  tripUpdates: TripUpdate[];
  /** Service day the trip-updates are for, along with the day before for its night trips. */
  today: string;
  now: number;
}

/** A trip running on estimated times, or not running at all. */
export interface TripOverride {
  canceled: boolean;
  arrivals: number[];
  departures: number[];
  /** Stops of the pattern the trip skips. */
  skipped: boolean[];
  realtime: boolean;
}

export type RealtimeOverlay = Map<string, TripOverride>;

/** A vehicle reports a trip_id only: it drives the day's run in progress, not another day's. */
function isRunning(trip: NetworkTrip, now: number): boolean {
  const HOUR = 3600;
  return trip.departures[0] - HOUR <= now && now <= trip.arrivals[trip.arrivals.length - 1] + HOUR;
}

/** The trip-updates are restored from the last session: yesterday's must not reach today's runs. */
function appliesOn(tripUpdate: TripUpdate, serviceDate: string): boolean {
  return tripUpdate.date <= serviceDate && serviceDate <= (tripUpdate.end_date ?? tripUpdate.date);
}

/** Trips outside the service-day path have no service_date: the update is kept as is. */
export function tripUpdateOnServiceDate(
  tripUpdate: TripUpdate | undefined,
  serviceDate: string | undefined,
): TripUpdate | undefined {
  return tripUpdate && (!serviceDate || appliesOn(tripUpdate, serviceDate)) ? tripUpdate : undefined;
}

/** Past midnight, the updates of the day before are loaded too: its night trips still run (#293). */
function hasTripUpdatesOn(serviceDate: string, snapshot: RealtimeSnapshot): boolean {
  return serviceDate === snapshot.today || serviceDate === previousGtfsDate(snapshot.today);
}

export function tripRealtime(trip: NetworkTrip, snapshot: RealtimeSnapshot): TripRealtime | undefined {
  const device = snapshot.devices.find(
    d => d.trip_id === trip.trip.trip_id && !d.trip_pending && isRunning(trip, snapshot.now),
  );
  const tripUpdate = hasTripUpdatesOn(trip.serviceDate, snapshot)
    ? snapshot.tripUpdates.find(tu => tu.trip_id === trip.trip.trip_id && appliesOn(tu, trip.serviceDate))
    : undefined;
  if (!device && !tripUpdate) return undefined;
  return { device, tripUpdate };
}

export function tripOverride(trip: NetworkTrip, realtime: TripRealtime): TripOverride {
  const stopTimes = trip.trip.stop_times;
  const delays = stopTimes.map(st => stopDelay(st.stop_sequence, realtime) ?? 0);
  return {
    canceled: Boolean(realtime.tripUpdate?.is_canceled),
    arrivals: trip.arrivals.map((time, i) => time + delays[i]),
    departures: trip.departures.map((time, i) => time + delays[i]),
    skipped: stopTimes.map(st => !isStopServed(st.stop_sequence, realtime)),
    realtime: realtime.device?.delay != null || Boolean(realtime.tripUpdate),
  };
}

/** Estimated times of every trip with live data, computed once per search. */
export function buildRealtimeOverlay(network: Network, snapshot: RealtimeSnapshot): RealtimeOverlay {
  const overlay: RealtimeOverlay = new Map();
  const tripIds = new Set<string>();
  snapshot.devices.forEach(device => device.trip_id && tripIds.add(device.trip_id));
  snapshot.tripUpdates.forEach(tripUpdate => tripIds.add(tripUpdate.trip_id));
  if (tripIds.size === 0) return overlay;

  network.patterns.forEach(pattern =>
    pattern.trips.forEach(trip => {
      if (!tripIds.has(trip.trip.trip_id)) return;
      const realtime = tripRealtime(trip, snapshot);
      if (realtime) overlay.set(trip.key, tripOverride(trip, realtime));
    }),
  );
  return overlay;
}
